import {ArrowRight, BarChart3, CheckCircle2, Network, ShieldAlert} from 'lucide-react';

export type ArticleDecisionProfile = {
  path: string;
  kind: string;
  category: string;
  verdict: string;
  recommendedFor: string;
  caution: string;
  entities: string[];
  updatedIso: string;
  updatedLabel: string;
  connections: Array<{label: string; href: string}>;
};

type ArticleEditorialScore = {
  label: string;
  score: number;
  context: string;
};

type ArticleScoreProfile = {
  version: string;
  summary: string;
  scores: ArticleEditorialScore[];
};

const profiles: ArticleDecisionProfile[] = [
  {
    path: '/para/monitor-para-ps5/', kind: 'Guia por perfil', category: 'Monitores e PlayStation',
    verdict: 'QHD de 27 polegadas com 120 Hz e VRR é o melhor equilíbrio para a maioria; 4K de 32 polegadas com HDMI 2.1 é a escolha premium para máxima definição.',
    recommendedFor: 'Quem quer escolher monitor para PS5 pelo tamanho da tela, distância, tipo de jogo e uso compartilhado com PC.',
    caution: 'O PS5 não passa de 120 Hz; confirme resolução, frequência e VRR especificamente pela entrada HDMI do modelo.',
    entities: ['PlayStation 5', 'Monitor QHD 120 Hz', 'Monitor 4K 120 Hz', 'VRR', 'HDMI 2.1'], updatedIso: '2026-08-02', updatedLabel: '2 ago 2026',
    connections: [{label: 'Monitor OLED ou IPS', href: '/comparar/monitor-oled-vs-ips/'}, {label: 'PS5 vale a pena', href: '/games/playstation-5/'}],
  },
  {
    path: '/audio/fifine-am8/', kind: 'Ficha permanente', category: 'Áudio para criadores',
    verdict: 'O FIFINE AM8 vale a pena para voz, podcast, streaming e chamadas: começa completo por USB e preserva um caminho de upgrade por XLR. O resultado depende de falar perto da cápsula.',
    recommendedFor: 'Criadores, jogadores e profissionais que querem voz presente em um quarto comum e possibilidade de evoluir o setup.',
    caution: 'Não é ideal para uso distante, e os controles do corpo ficam inativos no modo XLR.',
    entities: ['FIFINE AmpliGame AM8', 'Microfone dinâmico', 'USB-C', 'XLR'], updatedIso: '2026-07-30', updatedLabel: '30 jul 2026',
    connections: [{label: 'Tecnologia para criadores', href: '/criadores-de-conteudo'}, {label: 'Monitor OLED ou IPS', href: '/comparar/monitor-oled-vs-ips/'}],
  },  {
    path: '/comparar/ps5-fisico-vs-digital/', kind: 'Comparação de versões', category: 'Consoles',
    verdict: 'Para a maioria das pessoas, o PS5 com leitor oferece a compra mais segura porque amplia as opções de preço, empréstimo e revenda de jogos.',
    recommendedFor: 'Quem quer liberdade para alternar entre mídia física e digital.',
    caution: 'O Digital Edition só compensa quando a diferença de preço é relevante e você já abandonou discos.',
    entities: ['PlayStation 5', 'PS5 Digital Edition', 'Mídia física'], updatedIso: '2026-07-30', updatedLabel: '30 jul 2026',
    connections: [{label: 'PS5 vale a pena?', href: '/games/playstation-5/'}, {label: 'PS5 ou Series S', href: '/comparar/ps5-vs-xbox-series-s/'}],
  },
  {
    path: '/games/playstation-5/', kind: 'Decisão de compra', category: 'Consoles',
    verdict: 'O PS5 continua sendo uma compra sólida para quem quer jogar agora; esperar uma futura geração só faz sentido quando não há urgência nem biblioteca desejada.',
    recommendedFor: 'Quem valoriza a biblioteca atual, desempenho consistente e jogos de grande produção.',
    caution: 'Adie a compra se o orçamento estiver apertado ou se poucos jogos atuais realmente interessarem.',
    entities: ['PlayStation 5', 'PlayStation 6', 'Ciclo de consoles'], updatedIso: '2026-07-30', updatedLabel: '30 jul 2026',
    connections: [{label: 'Físico ou digital', href: '/comparar/ps5-fisico-vs-digital/'}, {label: 'Qual console comprar', href: '/melhores/consoles/'}],
  },
  {
    path: '/assinaturas/game-pass/', kind: 'Análise de assinatura', category: 'Serviços',
    verdict: 'Game Pass pode valer para quem joga pouco quando a assinatura é concentrada nos meses de uso; manter o plano o ano inteiro sem jogar dilui o benefício.',
    recommendedFor: 'Quem alterna períodos de jogo e quer experimentar títulos sem comprá-los separadamente.',
    caution: 'Evite a renovação automática quando sua rotina passa semanas sem abrir um jogo.',
    entities: ['Xbox Game Pass', 'Game Pass Ultimate', 'Game Pass Premium'], updatedIso: '2026-07-30', updatedLabel: '30 jul 2026',
    connections: [{label: 'PS Plus ou Game Pass', href: '/comparar/ps-plus-vs-game-pass/'}, {label: 'Xbox Series S casual', href: '/games/xbox-series-s/'}],
  },
  {
    path: '/comparar/ps-plus-vs-game-pass/', kind: 'Comparação de serviços', category: 'Assinaturas',
    verdict: 'A melhor assinatura é definida primeiro pelo console e depois pelo catálogo: Game Pass favorece variedade e acesso amplo; PS Plus ganha valor dentro do ecossistema PlayStation.',
    recommendedFor: 'Quem quer escolher o serviço pela rotina, pelo console e pelos jogos que pretende terminar.',
    caution: 'Não compare apenas o número de jogos: disponibilidade, plano e tempo livre mudam o valor real.',
    entities: ['PlayStation Plus', 'Xbox Game Pass', 'Assinaturas de jogos'], updatedIso: '2026-07-30', updatedLabel: '30 jul 2026',
    connections: [{label: 'Game Pass para quem joga pouco', href: '/assinaturas/game-pass/'}, {label: 'PS5 ou Series S', href: '/comparar/ps5-vs-xbox-series-s/'}],
  },
  {
    path: '/games/nintendo-switch-2/', kind: 'Ficha de decisão', category: 'Consoles',
    verdict: 'O Switch 2 vale mais para quem prioriza jogos da Nintendo, portabilidade e uso compartilhado; potência bruta e economia em jogos apontam para outras opções.',
    recommendedFor: 'Famílias, jogadores portáteis e quem acompanha as franquias da Nintendo.',
    caution: 'Considere o custo de jogos, acessórios e armazenamento antes de olhar apenas o preço do console.',
    entities: ['Nintendo Switch 2', 'Jogos Nintendo', 'Console portátil'], updatedIso: '2026-07-30', updatedLabel: '30 jul 2026',
    connections: [{label: 'Switch 2 ou PS5', href: '/comparar/nintendo-switch-2-vs-ps5/'}, {label: 'Precisa de microSD?', href: '/games/nintendo-switch-2/microsd-express/'}],
  },
  {
    path: '/games/nintendo-switch-2/quando-comprar/', kind: 'Momento de compra', category: 'Consoles',
    verdict: 'Comprar agora faz sentido quando há jogos para aproveitar imediatamente e o custo total cabe no orçamento; esperar é melhor quando a decisão depende de desconto.',
    recommendedFor: 'Quem já escolheu jogos, encontrou vendedor confiável e não comprometerá outras despesas.',
    caution: 'Black Friday não garante o menor preço; acompanhe o valor do conjunto completo, não só do console.',
    entities: ['Nintendo Switch 2', 'Black Friday', 'Mario Kart World'], updatedIso: '2026-07-30', updatedLabel: '30 jul 2026',
    connections: [{label: 'Switch 2 vale a pena', href: '/games/nintendo-switch-2/'}, {label: 'Switch 2 ou PS5', href: '/comparar/nintendo-switch-2-vs-ps5/'}],
  },
  {
    path: '/games/nintendo-switch-2/microsd-express/', kind: 'Decisão de acessório', category: 'Armazenamento',
    verdict: 'O microSD Express não é obrigatório no primeiro dia para todos, mas se torna importante para bibliotecas digitais grandes; cartões microSD comuns não substituem o padrão exigido.',
    recommendedFor: 'Quem compra jogos digitais e pretende manter vários títulos instalados.',
    caution: 'Não compre pela capacidade apenas: confirme explicitamente o padrão microSD Express.',
    entities: ['Nintendo Switch 2', 'microSD Express', 'Armazenamento digital'], updatedIso: '2026-07-30', updatedLabel: '30 jul 2026',
    connections: [{label: 'Switch 2 vale a pena', href: '/games/nintendo-switch-2/'}, {label: 'Comprar agora ou esperar', href: '/games/nintendo-switch-2/quando-comprar/'}],
  },
  {
    path: '/comparar/nintendo-switch-2-vs-ps5/', kind: 'Comparação de consoles', category: 'Consoles',
    verdict: 'Switch 2 vence em mobilidade e jogos Nintendo; PS5 vence em potência, experiência de sala e biblioteca cinematográfica. O jeito de jogar decide melhor que a ficha técnica.',
    recommendedFor: 'Quem consegue definir se portabilidade ou desempenho na TV é a prioridade real.',
    caution: 'Some jogos, assinatura, controles e armazenamento antes de comparar o custo final.',
    entities: ['Nintendo Switch 2', 'PlayStation 5', 'Perfil de jogador'], updatedIso: '2026-07-30', updatedLabel: '30 jul 2026',
    connections: [{label: 'Qual console comprar', href: '/melhores/consoles/'}, {label: 'Switch 2 vale a pena', href: '/games/nintendo-switch-2/'}],
  },
  {
    path: '/comparar/ps5-vs-xbox-series-s/', kind: 'Comparação de consoles', category: 'Consoles',
    verdict: 'PS5 entrega mais potência e liberdade com mídia física; Series S reduz a entrada e combina melhor com Game Pass, mas exige aceitar limites de armazenamento e desempenho.',
    recommendedFor: 'Quem quer escolher entre investimento inicial menor e experiência mais completa na TV.',
    caution: 'A economia do Series S diminui se armazenamento adicional e jogos fora da assinatura entrarem na conta.',
    entities: ['PlayStation 5', 'Xbox Series S', 'Game Pass'], updatedIso: '2026-07-30', updatedLabel: '30 jul 2026',
    connections: [{label: 'PS5 físico ou digital', href: '/comparar/ps5-fisico-vs-digital/'}, {label: 'Series S para casual', href: '/games/xbox-series-s/'}],
  },
  {
    path: '/melhores/consoles/', kind: 'Guia por perfil', category: 'Consoles',
    verdict: 'PS5 é a escolha equilibrada para sala e grandes produções, Switch 2 para mobilidade e exclusivos Nintendo, e Series S para entrada econômica com assinatura.',
    recommendedFor: 'Quem quer partir do próprio perfil em vez de escolher pelo console mais comentado.',
    caution: 'Nenhum vencedor serve para todos; biblioteca, TV, mobilidade e orçamento mudam a resposta.',
    entities: ['PlayStation 5', 'Xbox Series S', 'Nintendo Switch 2'], updatedIso: '2026-07-30', updatedLabel: '30 jul 2026',
    connections: [{label: 'Switch 2 ou PS5', href: '/comparar/nintendo-switch-2-vs-ps5/'}, {label: 'PS5 ou Series S', href: '/comparar/ps5-vs-xbox-series-s/'}],
  },
  {
    path: '/comparar/kindle-scribe-vs-tablet-para-estudar/', kind: 'Comparação para estudo', category: 'Leitura e produtividade',
    verdict: 'Kindle Scribe favorece foco, leitura e anotações sem distração; tablet é mais versátil para aulas, aplicativos, pesquisa e trabalho multimídia.',
    recommendedFor: 'Quem sabe se precisa de concentração em leitura ou de aplicativos completos.',
    caution: 'Não escolha tela e caneta sem considerar arquivos, aulas, multitarefa e fluxo de estudo.',
    entities: ['Kindle Scribe', 'Samsung Galaxy Tab S10 FE', 'Estudo digital'], updatedIso: '2026-07-30', updatedLabel: '30 jul 2026',
    connections: [{label: 'Paperwhite ou Scribe', href: '/comparar/kindle-paperwhite-vs-kindle-scribe/'}, {label: 'Tab S10 FE para estudar', href: '/tablets/samsung-galaxy-tab-s10-fe/'}],
  },
  {
    path: '/comparar/kindle-paperwhite-vs-kindle-scribe/', kind: 'Comparação de leitores', category: 'Leitura digital',
    verdict: 'Paperwhite é a escolha mais simples para leitura portátil; Scribe vale o investimento quando PDFs grandes e escrita manual fazem parte da rotina.',
    recommendedFor: 'Leitores que conseguem separar portabilidade de necessidade real de anotar.',
    caution: 'O Scribe não substitui um tablet completo para quem depende de aplicativos e multitarefa.',
    entities: ['Kindle Paperwhite', 'Kindle Scribe', 'Leitor digital'], updatedIso: '2026-07-30', updatedLabel: '30 jul 2026',
    connections: [{label: 'Scribe ou tablet', href: '/comparar/kindle-scribe-vs-tablet-para-estudar/'}, {label: 'Tab S10 FE para estudar', href: '/tablets/samsung-galaxy-tab-s10-fe/'}],
  },
  {
    path: '/tablets/samsung-galaxy-tab-s10-fe/', kind: 'Ficha para estudo', category: 'Tablets',
    verdict: 'O Galaxy Tab S10 FE é uma opção equilibrada para estudo com caneta e aplicativos, desde que mobilidade e integração Android importem mais que desempenho de notebook.',
    recommendedFor: 'Estudantes que anotam, leem PDFs, assistem aulas e usam aplicativos Android.',
    caution: 'Não trate o tablet como substituto automático de notebook para softwares profissionais.',
    entities: ['Samsung Galaxy Tab S10 FE', 'S Pen', 'Samsung DeX'], updatedIso: '2026-07-30', updatedLabel: '30 jul 2026',
    connections: [{label: 'Kindle Scribe ou tablet', href: '/comparar/kindle-scribe-vs-tablet-para-estudar/'}, {label: 'Paperwhite ou Scribe', href: '/comparar/kindle-paperwhite-vs-kindle-scribe/'}],
  },
  {
    path: '/celulares/samsung-galaxy-a57/', kind: 'Ficha permanente', category: 'Smartphones',
    verdict: 'O Galaxy A57 vale a pena para quem prioriza tela, construção e longevidade, mas perde valor quando custa mais de 20% acima do Galaxy A56 equivalente.',
    recommendedFor: 'Quem pretende ficar vários anos com o aparelho e valoriza suporte prolongado.',
    caution: 'Para jogos pesados, zoom dedicado ou carregamento muito rápido, há alternativas mais adequadas.',
    entities: ['Samsung Galaxy A57 5G', 'Samsung Galaxy A56 5G', 'Smartphone intermediário'], updatedIso: '2026-07-30', updatedLabel: '30 jul 2026',
    connections: [{label: 'A57 vs. A56', href: '/comparar/galaxy-a57-vs-galaxy-a56/'}, {label: 'Hub de celulares', href: '/celulares/'}],
  },
  {
    path: '/comparar/galaxy-a57-vs-galaxy-a56/', kind: 'Comparação de celulares', category: 'Smartphones',
    verdict: 'O A57 é preferível quando a diferença é pequena; o A56 se torna a compra mais racional quando entrega armazenamento equivalente por pelo menos 20% menos.',
    recommendedFor: 'Quem compara preço na mesma capacidade e considera tempo de uso.',
    caution: 'Não compare 128 GB com 256 GB como se fossem ofertas equivalentes.',
    entities: ['Samsung Galaxy A57 5G', 'Samsung Galaxy A56 5G'], updatedIso: '2026-07-30', updatedLabel: '30 jul 2026',
    connections: [{label: 'Ficha do Galaxy A57', href: '/celulares/samsung-galaxy-a57/'}, {label: 'Samsung no GuiaSite', href: '/marcas/samsung/'}],
  },
  {
    path: '/comparar/monitor-oled-vs-ips/', kind: 'Comparação de tecnologia', category: 'Monitores',
    verdict: 'OLED é superior em contraste e movimento; IPS continua mais racional para texto, interfaces estáticas, ambientes claros e orçamento controlado.',
    recommendedFor: 'Quem separa uso de jogos e vídeo do trabalho prolongado com elementos fixos.',
    caution: 'Brilho, garantia, clareza de texto e risco de retenção importam tanto quanto a taxa de atualização.',
    entities: ['Monitor OLED', 'Monitor IPS', 'Samsung Odyssey G5'], updatedIso: '2026-07-30', updatedLabel: '30 jul 2026',
    connections: [{label: 'Tecnologia para criadores', href: '/criadores-de-conteudo'}, {label: 'Calculadora de PPI', href: '/ferramentas/calculadora-de-ppi'}],
  },
  {
    path: '/games/xbox-series-s/', kind: 'Ficha por perfil', category: 'Consoles',
    verdict: 'O Series S ainda faz sentido para jogador casual que aceita o formato digital e usa Game Pass; deixa de ser bom negócio quando armazenamento e qualidade 4K são prioridades.',
    recommendedFor: 'Quem joga ocasionalmente, quer gastar menos e prefere catálogo por assinatura.',
    caution: 'Armazenamento limitado e ausência de leitor podem aumentar o custo no longo prazo.',
    entities: ['Xbox Series S', 'Xbox Game Pass', 'Jogador casual'], updatedIso: '2026-07-30', updatedLabel: '30 jul 2026',
    connections: [{label: 'PS5 ou Series S', href: '/comparar/ps5-vs-xbox-series-s/'}, {label: 'Game Pass para quem joga pouco', href: '/assinaturas/game-pass/'}],
  },
  {
    path: '/comparar/cadeira-gamer-vs-cadeira-de-escritorio/', kind: 'Comparação ergonômica', category: 'Setup',
    verdict: 'Uma boa cadeira de escritório costuma entregar ergonomia mais consistente; cadeira gamer só vence quando oferece ajustes reais, estrutura adequada e encaixe correto ao corpo.',
    recommendedFor: 'Quem escolhe por ajuste, apoio e medidas, não apenas por aparência.',
    caution: 'Nenhuma cadeira corrige horas seguidas na mesma postura; pausas e regulagem continuam essenciais.',
    entities: ['Cadeira de escritório', 'Cadeira gamer', 'Ergonomia'], updatedIso: '2026-07-30', updatedLabel: '30 jul 2026',
    connections: [{label: 'Tecnologia para criadores', href: '/criadores-de-conteudo'}, {label: 'Ferramentas gratuitas', href: '/ferramentas-gratuitas'}],
  },
];

const scoresByPath: Record<string, ArticleScoreProfile> = {
  '/para/monitor-para-ps5/': {
    version: '1.1', summary: 'Notas por compatibilidade com PS5, qualidade de imagem, custo relativo, tamanho e versatilidade.',
    scores: [
      {label: '32” 4K 120 Hz', score: 94, context: 'Máxima definição e melhor longevidade visual; exige HDMI 2.1 bem implementado.'},
      {label: '27” QHD 120 Hz', score: 92, context: 'Melhor equilíbrio para mesa, nitidez, fluidez e custo.'},
      {label: '24” Full HD 120 Hz', score: 80, context: 'Alternativa econômica para jogos competitivos e orçamento curto.'},
    ],
  },
  '/audio/fifine-am8/': {
    version: '1.1', summary: 'Nota para voz, criação de conteúdo e capacidade de evolução do setup.',
    scores: [{label: 'FIFINE AM8', score: 89, context: 'Excelente por USB e preparado para XLR; exige uso próximo.'}],
  },
  '/comparar/ps5-fisico-vs-digital/': {
    version: '1.1', summary: 'Notas consideram liberdade de compra, custo total e vida útil da biblioteca.',
    scores: [
      {label: 'PS5 com leitor', score: 89, context: 'Maior liberdade com discos, usados e revenda.'},
      {label: 'PS5 Digital', score: 80, context: 'Bom quando a economia inicial é realmente relevante.'},
    ],
  },
  '/games/playstation-5/': {
    version: '1.1', summary: 'Nota do console dentro do cenário de compra de 2026.',
    scores: [{label: 'PlayStation 5', score: 87, context: 'Biblioteca forte e experiência consistente; preço define o momento.'}],
  },
  '/assinaturas/game-pass/': {
    version: '1.1', summary: 'Nota aplicada ao uso de quem joga pouco e concentra a assinatura.',
    scores: [{label: 'Game Pass', score: 78, context: 'Competente por períodos; perde valor na renovação sem uso.'}],
  },
  '/comparar/ps-plus-vs-game-pass/': {
    version: '1.1', summary: 'Notas por catálogo, acesso, ecossistema e valor para o assinante ativo.',
    scores: [
      {label: 'Game Pass', score: 86, context: 'Variedade e acesso amplo favorecem experimentação.'},
      {label: 'PS Plus', score: 83, context: 'Forte dentro do PlayStation e dependente do plano certo.'},
    ],
  },
  '/games/nintendo-switch-2/': {
    version: '1.1', summary: 'Nota por portabilidade, biblioteca, uso familiar e custo completo.',
    scores: [{label: 'Nintendo Switch 2', score: 86, context: 'Excelente para mobilidade e jogos Nintendo; acessórios pesam.'}],
  },
  '/games/nintendo-switch-2/quando-comprar/': {
    version: '1.1', summary: 'Nota do Switch 2 quando há jogos imediatos e custo total controlado.',
    scores: [{label: 'Switch 2 agora', score: 84, context: 'Recomendado com jogo desejado e oferta confiável.'}],
  },
  '/games/nintendo-switch-2/microsd-express/': {
    version: '1.1', summary: 'Nota do acessório para biblioteca digital em expansão.',
    scores: [{label: 'microSD Express', score: 83, context: 'Útil para muitos jogos digitais; dispensável no primeiro dia para alguns.'}],
  },
  '/comparar/nintendo-switch-2-vs-ps5/': {
    version: '1.1', summary: 'Notas por experiência principal: mobilidade contra desempenho na TV.',
    scores: [
      {label: 'PlayStation 5', score: 88, context: 'Mais forte na TV, potência e grandes produções.'},
      {label: 'Nintendo Switch 2', score: 86, context: 'Mais forte em portabilidade, família e exclusivos Nintendo.'},
    ],
  },
  '/comparar/ps5-vs-xbox-series-s/': {
    version: '1.1', summary: 'Notas equilibram investimento inicial, desempenho e custo no longo prazo.',
    scores: [
      {label: 'PlayStation 5', score: 88, context: 'Experiência mais completa e opção de mídia física.'},
      {label: 'Xbox Series S', score: 82, context: 'Entrada econômica com limites claros de armazenamento e imagem.'},
    ],
  },
  '/melhores/consoles/': {
    version: '1.1', summary: 'Notas por adequação ao perfil; não existe vencedor universal.',
    scores: [
      {label: 'PlayStation 5', score: 88, context: 'Equilíbrio para jogar na TV.'},
      {label: 'Switch 2', score: 86, context: 'Mobilidade e jogos Nintendo.'},
      {label: 'Series S', score: 82, context: 'Menor entrada e assinatura.'},
    ],
  },
  '/comparar/kindle-scribe-vs-tablet-para-estudar/': {
    version: '1.1', summary: 'Notas consideram foco, escrita, PDFs, aplicativos e versatilidade.',
    scores: [
      {label: 'Tablet', score: 88, context: 'Mais versátil para estudo multimídia e aplicativos.'},
      {label: 'Kindle Scribe', score: 85, context: 'Superior para foco, leitura e escrita sem distrações.'},
    ],
  },
  '/comparar/kindle-paperwhite-vs-kindle-scribe/': {
    version: '1.1', summary: 'Notas por leitura portátil e necessidade real de escrita em tela grande.',
    scores: [
      {label: 'Kindle Paperwhite', score: 90, context: 'Referência para leitura portátil.'},
      {label: 'Kindle Scribe', score: 85, context: 'Mais completo para PDFs e anotações, porém maior e mais caro.'},
    ],
  },
  '/tablets/samsung-galaxy-tab-s10-fe/': {
    version: '1.1', summary: 'Nota para estudo, escrita com caneta, aplicativos e mobilidade.',
    scores: [{label: 'Galaxy Tab S10 FE', score: 86, context: 'Equilibrado para estudo; não substitui todo notebook.'}],
  },
  '/celulares/samsung-galaxy-a57/': {
    version: '1.1', summary: 'Nota por experiência, câmera, desempenho, bateria, longevidade e valor.',
    scores: [{label: 'Galaxy A57 5G', score: 82, context: 'Recomendado quando a diferença para o A56 é pequena.'}],
  },
  '/comparar/galaxy-a57-vs-galaxy-a56/': {
    version: '1.1', summary: 'Notas consideram a mesma capacidade e a diferença real de preço.',
    scores: [
      {label: 'Galaxy A57', score: 82, context: 'Mais novo e longevo; depende da diferença de preço.'},
      {label: 'Galaxy A56', score: 85, context: 'Melhor valor quando custa ao menos 20% menos.'},
    ],
  },
  '/comparar/monitor-oled-vs-ips/': {
    version: '1.1', summary: 'Notas separadas pelo uso predominante, não por superioridade absoluta.',
    scores: [
      {label: 'OLED para jogos e vídeo', score: 91, context: 'Referência em contraste e movimento.'},
      {label: 'IPS para trabalho', score: 88, context: 'Mais racional para texto e interfaces estáticas.'},
    ],
  },
  '/games/xbox-series-s/': {
    version: '1.1', summary: 'Nota aplicada ao jogador casual que aceita biblioteca digital.',
    scores: [{label: 'Xbox Series S', score: 82, context: 'Boa entrada com Game Pass; armazenamento limita.'}],
  },
  '/comparar/cadeira-gamer-vs-cadeira-de-escritorio/': {
    version: '1.1', summary: 'Notas por ergonomia, ajustes, materiais e adequação corporal.',
    scores: [
      {label: 'Cadeira ergonômica', score: 89, context: 'Escolha mais consistente para uso prolongado.'},
      {label: 'Cadeira gamer', score: 76, context: 'Só se destaca quando oferece ajustes e encaixe reais.'},
    ],
  },
};

const profileByPath = new Map(profiles.map((profile) => [profile.path, profile]));

function normalizePath(pathname: string) {
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
}

export function getArticleDecisionProfile(pathname: string) {
  const normalized = normalizePath(pathname);
  return profileByPath.get(pathname) ?? profileByPath.get(normalized) ?? profileByPath.get(normalized + '/');
}

export function ArticleDecisionStrip() {
  const profile = getArticleDecisionProfile(window.location.pathname);
  if (!profile) return null;
  const scoreProfile = scoresByPath[profile.path];

  return (
    <aside className="article-decision-strip" aria-label="Resumo da decisão">
      <div className="article-decision-strip__inner">
        <div className="article-decision-verdict">
          <span>{profile.kind} · {profile.category}</span>
          <strong>Veredito GuiaSite</strong>
          <p>{profile.verdict}</p>
        </div>
        <div className="article-decision-facts">
          <div>
            <CheckCircle2 size={20} aria-hidden="true" />
            <span><small>Recomendamos para</small><strong>{profile.recommendedFor}</strong></span>
          </div>
          <div>
            <ShieldAlert size={20} aria-hidden="true" />
            <span><small>Principal ressalva</small><strong>{profile.caution}</strong></span>
          </div>
        </div>
        <div className="article-decision-connections">
          <span><Network size={17} aria-hidden="true" /> Conexões</span>
          {profile.connections.map((connection) => (
            <a href={connection.href} key={connection.href}>{connection.label}<ArrowRight size={15} aria-hidden="true" /></a>
          ))}
          <small>Revisado em {profile.updatedLabel}</small>
        </div>
      </div>
      {scoreProfile ? (
        <section className="article-score-system" aria-label="GuiaSite Score">
          <div className="article-score-system__intro">
            <span><BarChart3 size={18} aria-hidden="true" /> GuiaSite Score v{scoreProfile.version}</span>
            <p>{scoreProfile.summary}</p>
            <a href="/metodologia/guiasite-score/">Como calculamos <ArrowRight size={15} aria-hidden="true" /></a>
          </div>
          <div className={`article-score-system__scores score-count-${scoreProfile.scores.length}`}>
            {scoreProfile.scores.map((item) => (
              <div className="article-score-item" key={item.label}>
                <strong>{item.score}<small>/100</small></strong>
                <span><b>{item.label}</b><small>{item.context}</small></span>
              </div>
            ))}
          </div>
          <p className="article-score-system__note">Nota editorial por adequação ao perfil. Não é média de usuários nem promessa de preço.</p>
        </section>
      ) : null}
    </aside>
  );
}